import React from 'react';

const NUCLEOTIDE_COLORS = [
  { color: '#DA70D6', label: 'A', desc: 'Adenine' },
  { color: '#FF6347', label: 'T', desc: 'Thymine' },
  { color: '#87CEEB', label: 'G', desc: 'Guanine' },
  { color: '#90EE90', label: 'C', desc: 'Cytosine' },
  { color: '#D3D3D3', label: 'N', desc: 'Unable to identify' },
  { color: '#ffffff', label: '—', desc: 'Gap', border: true },
];

const SECTIONS = [
  {
    icon: '🧬',
    title: 'Overview',
    content: `The Sequence Alignment viewer displays the pairwise alignment between a query sequence (QSEQ) and a subject sequence (SSEQ) from a BLAST hit. Each nucleotide is colour-coded by base type. Matching positions are shown with a | symbol, mismatches are left blank, and gaps are rendered as dashes.`,
  },
  {
    icon: '🎨',
    title: 'Nucleotide Color Coding',
    nucleotideColors: true,
  },
  {
    icon: '📋',
    title: 'Fields Displayed',
    rows: [
      ['qseqid',       'Query sequence identifier — the read or contig that was searched.'],
      ['sseqid',       'Subject sequence identifier — the reference entry that was matched.'],
      ['pident',       'Percent identity: the percentage of aligned positions that match exactly.'],
      ['len',          'Alignment length: total columns in the alignment (matches + mismatches + gaps).'],
      ['mm',           'Mismatches: number of positions with non-identical residues.'],
      ['gap',          'Gaps: number of gap openings in the alignment.'],
      ['qstart / qend','Start and end positions of the alignment on the query sequence.'],
      ['sstart / send','Start and end positions of the alignment on the subject sequence.'],
      ['evalue',       'Expect value: statistical significance of the alignment (lower is better).'],
      ['bit',          'Bit score: a normalized score measuring alignment quality (higher is better).'],
      ['qseq',         'The aligned portion of the query sequence (may contain gaps as "–").'],
      ['sseq',         'The aligned portion of the subject sequence (may contain gaps as "–").'],
    ],
  },
  {
    icon: '📐',
    title: 'Alignment Blocks',
    content: `The alignment is split into fixed-width blocks (60 residues per line). Each block shows the query on top, a middle row of match symbols (| for match, space for mismatch/gap), and the subject on the bottom. Position numbers are shown at the start and end of each line.`,
  },
  {
    icon: '📤',
    title: 'Export as PDF',
    content: `Click Export as PDF in the modal footer to download a snapshot of the current alignment view. The PDF captures the full content including colour highlighting. Use this to share or archive specific alignment results.`,
  },
  {
    icon: '💡',
    title: 'Interpretation Tips',
    rows: [
      ['High pident',  'Values ≥ 95% indicate very close matches — likely same serotype.'],
      ['Low evalue',   'Values < 1e-10 are considered highly significant alignments.'],
      ['Gap-heavy',    'Many gaps may indicate structural rearrangements or poor alignment quality.'],
      ['Short len',    'Very short alignments (< 50 bp) may be coincidental — check evalue carefully.'],
    ],
  },
];

export default function UserGuideSequenceAlignment({ open, onClose }) {
  if (!open) return null;

  return (
    <div className="rh-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="rh-modal-box rh-guide-modal" role="dialog" aria-modal="true" aria-labelledby="guide-title-seq">

        <div className="rh-modal-header">
          <div>
            <div className="rh-modal-title" id="guide-title-seq">Sequence Alignment — User Guide</div>
            <div className="rh-modal-subtitle">How to interpret QSEQ / SSEQ alignment results</div>
          </div>
          <button className="rh-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="rh-guide-body">
          {SECTIONS.map((sec, i) => (
            <div key={i} className="rh-guide-section">
              <div className="rh-guide-section-title">
                <span style={{ fontSize: 14, marginRight: 4 }}>{sec.icon}</span>
                {sec.title}
              </div>

              {sec.content && <p className="rh-guide-text">{sec.content}</p>}

              {sec.nucleotideColors && (
                <div className="rh-guide-color-legend">
                  {NUCLEOTIDE_COLORS.map(({ color, label, desc, border }) => (
                    <div key={label} className="rh-guide-color-row">
                      <span
                        className="rh-guide-color-swatch"
                        style={{
                          background: color,
                          border: border ? '1px solid #cbd5e1' : '1px solid rgba(0,0,0,0.12)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: 10,
                          color: '#334155',
                          fontFamily: 'var(--rh-mono)',
                          width: 22,
                          height: 22,
                          borderRadius: 4,
                          flexShrink: 0,
                        }}
                      >
                        {label}
                      </span>
                      <span className="rh-guide-color-label" style={{ minWidth: 80 }}>{label === '—' ? 'Gap (—)' : label}</span>
                      <span className="rh-guide-color-desc">{desc}</span>
                    </div>
                  ))}
                </div>
              )}

              {sec.rows && (
                <table className="rh-guide-table">
                  <tbody>
                    {sec.rows.map(([term, desc]) => (
                      <tr key={term}>
                        <td className="rh-guide-term">{term}</td>
                        <td className="rh-guide-desc">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>

        <div className="rh-modal-footer">
          <button className="rh-btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
